/**
 * CommunityContext — shared community + role state for the Volunteer Community
 * shell and its pages. Fetches the signed-in user's community once
 * (`GET /community/my`) and exposes the membership role + derived permission
 * flags so the sidebar (leader-only items) and pages (create/edit controls) can
 * gate UI without each re-fetching.
 *
 * All community endpoints need a `communityId`; consumers read `community?.id`.
 */
import { createContext, useContext, useCallback, useEffect, useState, type ReactNode } from 'react'
import { getMyCommunity, type CommunityResponse, type CommunityRole } from '@/services/communityApi'

interface CommunityContextValue {
  community: CommunityResponse | null
  myRole: CommunityRole | null
  isLeader: boolean
  canManage: boolean
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

const CommunityContext = createContext<CommunityContextValue | undefined>(undefined)

const MANAGE_ROLES: CommunityRole[] = ['LEADER', 'COORDINATOR', 'MODERATOR']

export function CommunityProvider({ children }: { children: ReactNode }) {
  const [community, setCommunity] = useState<CommunityResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const c = await getMyCommunity()
      setCommunity(c)
    } catch (err: any) {
      // 401 is handled by the api interceptor (redirect to /login).
      setError(err?.response?.data?.message || 'Could not load your community.')
      setCommunity(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void refresh() }, [refresh])

  const myRole = community?.myRole ?? null
  const isLeader = !!community?.isLeader || myRole === 'LEADER'
  const canManage = !!community?.canManage || (myRole !== null && MANAGE_ROLES.includes(myRole))

  return (
    <CommunityContext.Provider value={{ community, myRole, isLeader, canManage, loading, error, refresh }}>
      {children}
    </CommunityContext.Provider>
  )
}

export function useCommunity(): CommunityContextValue {
  const ctx = useContext(CommunityContext)
  if (!ctx) throw new Error('useCommunity must be used within a CommunityProvider')
  return ctx
}

export default CommunityContext